import { darken } from '@mui/material';
import { useEffect, useMemo } from 'react';
import ChartEmptyMessage from 'src/components/Charts/ChartEmptyMessage';
import { bulkColors } from 'src/constants/styles';
import ReBarChart from 'switch-fe-shared/components/Charts/ReBarChart';
import { capitalize, graphFileName } from 'switch-fe-shared/utils/formatting';
import { AggregateP1SplitByTagBucket } from 'switch-shared/modules/crud/utility-data/utility-data.dto';
import { UtilityTypeOrAll } from 'switch-shared/modules/crud/utility/utility.enums';
import { DurationUnit } from 'switch-shared/utils/duration.enum';

import { TAG_TYPES } from '../../../constants';
import { useApplications } from '../../../hooks/useApplications';
import { useGetAggregateP1SplitByTagQuery } from '../../../services/switch-rest-api/utility-data.service';
import { getUtilityUnits } from '../../../utils/formatters';

import '../../../styles/chart/LoadingChart.css';
import { isAllUtilitySelection, isUtilityType } from '../../../utils/selection';
import { Metrics } from '../../dashboard/components/widgets/UtilityUsageWidget';

interface Props {
  handleMetrics: (metrics: Metrics, priorMetrics?: Metrics) => void;
  appId: string;
  utilityType: UtilityTypeOrAll;
  startDate: Date;
  endDate: Date;
  bucketSize: number;
  bucketUnit: DurationUnit;
  zoneId?: string;
  onLoadingChange?: (isLoading: boolean) => void;
}

const TaggedUsageGraph = ({
  handleMetrics,
  appId,
  utilityType,
  startDate,
  endDate,
  bucketSize,
  bucketUnit,
  zoneId,
  onLoadingChange,
}: Props) => {
  const id = graphFileName(
    `tagged-p1-split_`,
    startDate.toISOString(),
    endDate.toISOString()
  );

  const { applications: apps } = useApplications();

  /* Hooks & Use Effects */

  /* States */

  /* API Calls */

  const { data, isLoading, isFetching, isUninitialized } =
    useGetAggregateP1SplitByTagQuery(
      {
        startTimeISO: startDate.toISOString(),
        endTimeISO: endDate.toISOString(),
        applicationId: appId,
        ...(isUtilityType(utilityType) && { utilityType }),
        bucketSize,
        bucketUnit,
        zoneId,
        tags: isAllUtilitySelection(utilityType)
          ? TAG_TYPES.map((t) => t._id)
          : TAG_TYPES.reduce((acc, t) => {
              if (t.utilities.includes(utilityType)) acc.push(t._id);
              return acc;
            }, [] as string[]),
        includePriorPeriod: true,
      },
      { pollingInterval: 300_000 }
    );

  const isGraphLoading = isLoading || isUninitialized;

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isGraphLoading);
      return () => {
        onLoadingChange(false);
      };
    }
    return undefined;
  }, [isGraphLoading, onLoadingChange]);

  /* Functions & Functional Components */

  const series = useMemo(() => {
    const buckets = data?.buckets ?? [];
    const tags = data?.tags ?? [];

    const createSeries = (
      name: string,
      color: string,
      group: 'bulk' | 'sub-meter',
      accessor: (b: AggregateP1SplitByTagBucket) => number | null
    ) => ({
      name,
      data: buckets.map((b) => accessor(b) ?? 0),
      color,
      group,
    });

    const createTagSeries = (
      baseName: string,
      group: 'bulk' | 'sub-meter',
      baseColor: string
    ) =>
      tags.reduce(
        (acc, tag, i) => {
          const totals = group === 'bulk' ? data?.bulk : data?.['sub-meter'];
          if (totals?.[tag] != null) {
            acc.push(
              createSeries(
                `${baseName} ${capitalize(tag)}`,
                darken(baseColor, (1 / Math.max(1, tags.length + 2)) * (i + 1)),
                group,
                (b) => {
                  const value = b[group][tag];
                  return value ? value / 1000 : 0;
                }
              )
            );
          }
          return acc;
        },
        [] as Array<{
          name: string;
          data: number[];
          color: string;
          group: 'bulk' | 'sub-meter';
        }>
      );

    return [
      // First: All bulk meters (untagged then tagged)
      ...(data?.bulk.untagged == null
        ? []
        : [
            createSeries('Bulk', bulkColors.BULK_COLOR, 'bulk', (b) =>
              b.bulk.untagged ? b.bulk.untagged / 1000 : 0
            ),
          ]),
      ...createTagSeries('Bulk', 'bulk', bulkColors.BULK_COLOR),
      // Second: All sub-meter meters (untagged then tagged)
      ...(data?.['sub-meter'].untagged == null
        ? []
        : [
            createSeries(
              'Sub-Meter',
              bulkColors.TENANT_COLOR,
              'sub-meter',
              (b) =>
                b['sub-meter'].untagged ? b['sub-meter'].untagged / 1000 : 0
            ),
          ]),
      ...createTagSeries('Sub-Meter', 'sub-meter', bulkColors.TENANT_COLOR),
      // Third: Unmetered usage (stacked on top of all sub-meter usage)
      createSeries('Unmetered', bulkColors.COMMUNAL_COLOR, 'sub-meter', (b) =>
        b.unmetered ? b.unmetered / 1000 : 0
      ),
    ];
  }, [data]);

  const categories: string[] = useMemo(
    () => (data ? data.buckets.map((b) => b.timestampISO) : []),
    [data]
  );

  const unit = useMemo(() => {
    return getUtilityUnits(utilityType, true);
  }, [utilityType]);

  const tooltipTotals = useMemo(
    () => [
      { label: 'Total Bulk', prefixes: ['Bulk'] },
      { label: 'Total Sub-Meter', prefixes: ['Sub-Meter'] },
      { label: 'Total Unmetered', prefixes: ['Unmetered'], alwaysShow: true },
    ],
    []
  );

  // Every label comes back null when the application has no meter points for
  // the selection (the split endpoint has no explicit error signal for this).
  const hasNoMeterPoints = useMemo(() => {
    if (!data) return false;
    return [data.bulk, data['sub-meter']].every(
      (group) =>
        group.untagged == null && data.tags.every((tag) => group[tag] == null)
    );
  }, [data]);

  useEffect(() => {
    const totalBulk = data?.bulk.total;
    const totalSub = data?.['sub-meter'].total;
    const totalCommunal = data?.unmetered;

    const priorBulk = data?.priorBulk?.total;
    const priorSub = data?.priorSubMeter?.total;
    const priorCommunal = data?.priorUnmetered;

    const currentMetrics = {
      bulk: totalBulk !== undefined ? totalBulk / 1000 : undefined,
      subMeter: totalSub !== undefined ? totalSub / 1000 : undefined,
      communal: totalCommunal !== undefined ? totalCommunal / 1000 : undefined,
    };

    const priorMetrics =
      priorBulk !== undefined ||
      priorSub !== undefined ||
      priorCommunal !== undefined
        ? {
            bulk: priorBulk !== undefined ? priorBulk / 1000 : undefined,
            subMeter: priorSub !== undefined ? priorSub / 1000 : undefined,
            communal:
              priorCommunal !== undefined ? priorCommunal / 1000 : undefined,
          }
        : undefined;

    handleMetrics(currentMetrics, priorMetrics);
  }, [data, handleMetrics]);

  return !isFetching && hasNoMeterPoints ? (
    <ChartEmptyMessage
      message={`No meter-points found for ${
        apps.find((a) => a._id === appId)?.name ?? 'Unknown application'
      } - ${utilityType}`}
    />
  ) : (
    <div
      className={isFetching ? 'pulsing-chart' : undefined}
      style={{ height: '100%', paddingTop: 25 }}
    >
      <ReBarChart
        key={id}
        series={series}
        isLoading={isLoading}
        categories={categories}
        colors={series.map((s) => s.color)}
        yTitle={`Usage (${unit})`}
        unit={unit}
        showLegend
        fileName={id}
        bucketOptions={{ unit: bucketUnit }}
        stackGroups
        barOpacity={1}
        tooltipTotals={tooltipTotals}
      />
    </div>
  );
};

export default TaggedUsageGraph;
